import { useEffect, useRef, useState } from 'react'
import { addYears } from 'date-fns'
import { useQuery } from 'urql'

import { Box } from 'ui-primitives/src/Box'
import { useBreakpoints } from 'ui-primitives/src/hooks/useBreakpoints'
import { Button } from 'flow-builder/src/components/form/Button'

import {
  type DateRange,
  DateRangePicker,
} from 'flows/hotel-new/booking-bar/DateRangePicker'
import { graphql } from 'gql'
import type { HotelEnumsHotelProperty } from 'gql/graphql'

import { defaultRoom, RoomConfigurator } from './RoomConfigurator'
import type { Room } from './urlParams'

const today = new Date()

type Popover = 'rooms' | 'dates'
type PopoverVisibility = Record<Popover, boolean>

type Props = {
  adultsOnly?: boolean
  initialDates?: Date[] | null
  initialRooms?: Room[] | null
  maxDate?: Date
  minDays?: number
  mobileButtonLabel?: string
  onChange?: (values: { rooms: Room[]; dates: Date[] }) => void
  onSubmit?: (values: { rooms: Room[]; dates: Date[] }) => void
  property: HotelEnumsHotelProperty
  submitButtonLabel: string
}

/**
 * BookingBar component
 *
 * The `BookingBar` component fetches availability data and allows the user to configure the number of rooms and guests.
 * The component integrates using popovers, on mobile devices that covers full screen and also provides navigation.
 *
 * @param props - The properties for the `BookingBar` component.
 * @param props.adultsOnly - Do not show children and infants pickers.
 * @param props.initialDates - The dates to show on first arrival.
 * @param props.initialRoom - The rooms to show on first arrival.
 * @param props.maxDate - The maximum selectable date for the booking.
 * @param props.minDays - The minimum required length for the date range selection.
 * @param props.mobileButtonLabel - The label for the submit button.
 * @param props.onChange - A callback function triggered on data change.
 * @param props.onSubmit - A callback function triggered on submit.
 * @param props.property - The property identifier.
 * @param props.submitButtonLabel - The label for the submit button.
 * @returns The rendered `BookingBar` component.
 */
export const BookingBar = ({
  adultsOnly = false,
  initialDates = [],
  initialRooms = [defaultRoom],
  maxDate = addYears(today, 2),
  minDays = 1,
  onChange,
  onSubmit,
  property,
  submitButtonLabel,
  mobileButtonLabel = submitButtonLabel,
}: Props) => {
  const { disabledDays, checkoutOnlyDays, firstAvailableDateRange, fetching } =
    useAvailability(property, today, maxDate, minDays)
  const { isMobile, mediaMd, mediaBmd } = useBreakpoints()
  const wrapperAnchor = useRef<HTMLDivElement>(null)
  const pickerAnchor = useRef<HTMLDivElement>(null)
  const roomAnchor = useRef<HTMLDivElement>(null)
  const isMediumScreen = mediaMd && !mediaBmd

  const [rooms, setRooms] = useState<Room[]>([defaultRoom])
  const [range, setRange] = useState<DateRange | undefined>()
  const [isInitialized, setInitialized] = useState(false)

  const [popoverVisibility, setPopoverVisibility] = useState<PopoverVisibility>(
    {
      rooms: false,
      dates: false,
    }
  )

  useEffect(() => {
    onChange?.({
      rooms,
      dates: range?.from && range?.to ? [range.from, range.to] : [],
    })
  }, [rooms, range])

  useEffect(() => {
    if (range) {
      return
    }

    if (initialDates?.length === 2) {
      setRange({
        from: initialDates[0],
        to: initialDates[1],
      })
    } else if (
      !fetching &&
      firstAvailableDateRange?.from &&
      firstAvailableDateRange?.to
    ) {
      setRange({
        from: new Date(firstAvailableDateRange.from),
        to: new Date(firstAvailableDateRange.to),
      })
    }
  }, [firstAvailableDateRange, fetching, initialDates, range])

  useEffect(() => {
    if (initialRooms?.length && !isInitialized) {
      setRooms(initialRooms)
      setInitialized(true)
    }
  }, [initialRooms, isInitialized])

  const showPopover = (popover: Popover) => {
    setPopoverVisibility({
      dates: false,
      rooms: false,
      [popover]: true,
    })
  }

  const handleSubmit = () => {
    if (!range?.to) {
      return // TODO: display a message to the user, under discussion with design
    }

    onSubmit?.({
      dates: [range.from, range.to],
      rooms,
    })
  }

  return (
    <Box boxShadow="var(--box-shadow)" ref={wrapperAnchor} position="relative">
      <Box
        flexDirection="row"
        gap="1px"
        background="var(--border-color)"
        position={{ xs: 'absolute', md: 'static' }}
        bottom={{ xs: '-100vh', md: 'auto' }} // on mobile we need the bar to be present but outside of the visible area
      >
        <Box flex="1" ref={roomAnchor}>
          <RoomConfigurator
            adultsOnly={adultsOnly}
            onChange={setRooms}
            value={rooms}
            popover={{
              open: popoverVisibility.rooms,
              setOpen: (open: boolean) => {
                setPopoverVisibility(prevState => ({
                  ...prevState,
                  rooms: open,
                }))
              },
              onNext: () => {
                showPopover('dates')
              },
              anchor: isMobile ? wrapperAnchor.current : roomAnchor.current,
            }}
          />
        </Box>
        <Box
          flex="2"
          ref={pickerAnchor}
          flexDirection="row"
          gap="1px"
          background="var(--border-color)"
        >
          <DateRangePicker
            popover={{
              open: popoverVisibility.dates,
              setOpen: (open: boolean) => {
                setPopoverVisibility(prevState => ({
                  ...prevState,
                  dates: open,
                }))
              },
              onBack: () => {
                showPopover('rooms')
              },
              onNext: () => {
                handleSubmit()
              },
              anchor: isMediumScreen
                ? wrapperAnchor.current
                : pickerAnchor.current,
            }}
            disabledDays={disabledDays}
            checkoutDays={checkoutOnlyDays}
            onChange={setRange}
            value={range}
            minDays={minDays}
            maxDate={maxDate}
          />
          <Box flex="0">
            <Button
              onClick={handleSubmit}
              preset="blue"
              style={{ whiteSpace: 'nowrap', flex: 1 }}
            >
              {submitButtonLabel}
            </Button>
          </Box>
        </Box>
      </Box>
      <Box display={{ xs: 'block', md: 'none' }}>
        <Button
          preset="blue"
          style={{ whiteSpace: 'nowrap', flex: 1, outline: 'none' }}
          onClick={() => {
            showPopover('rooms')
          }}
        >
          {mobileButtonLabel}
        </Button>
      </Box>
    </Box>
  )
}

/**
 * Fetches and processes room availability data for a given property and date range.
 *
 * @param property - The property identifier for which availability data is fetched.
 * @param start - The start of the range.
 * @param end - The end of the range.
 * @param minDays - The amount of minimum days required for the booking.
 * @returns An object containing:
 * - `disabledDays`: An array of dates that are disabled for selection.
 * - `checkoutOnlyDays`: An array of dates that are marked as checkout-only.
 */
const useAvailability = (
  property: HotelEnumsHotelProperty,
  start: Date,
  end: Date,
  minDays: number
) => {
  const from = start.toISOString()
  const to = end.toISOString()

  const [{ data, fetching }] = useQuery({
    query: ROOM_RANGE_CALENDAR_QUERY,
    variables: {
      input: {
        property,
        from,
        to,
        numberOfNights: minDays,
      },
    },
  })

  const { disabledDays, checkoutOnlyDays, firstAvailableDateRange } =
    data?.Bluelagoon?.Booking?.postCartHotelRangeAvailabilityCalendar ?? {}

  return {
    disabledDays: [
      { before: today },
      ...(disabledDays
        ?.filter((d): d is string => d !== null)
        .map(d => new Date(d)) ?? []),
      { after: end },
    ],
    checkoutOnlyDays:
      checkoutOnlyDays
        ?.filter((d): d is string => d !== null)
        .map(d => new Date(d)) ?? [],
    firstAvailableDateRange,
    fetching,
  }
}

const ROOM_RANGE_CALENDAR_QUERY = graphql(`
  query RoomRangeAvailabilityCalendar($input: HotelCartRoomUnavailabilityRangeModelInput!) {
    Bluelagoon {
      Booking {
        postCartHotelRangeAvailabilityCalendar(input: $input) {
          checkoutOnlyDays
          disabledDays
          firstAvailableDateRange {
            from
            to
          }
        }
      }
    }
  }
`)
